import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Fab,
  IconButton,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { buildPdfEmbedUrl } from "../../BrowseDesigns/components/detailsDialogUtils";
import {
  getAssetKey,
  getAssetKind,
  getAssetPreviewUrl,
  isSvgAsset,
} from "./galleryUtils";
import useNormalizedGalleryAsset from "./useNormalizedGalleryAsset";
import GalleryPlanOverlay from "./GalleryPlanOverlay";

function GalleryViewerDialog(props) {
  const {
    open,
    assets,
    activeIndex,
    onClose,
    onPrev,
    onNext,
    onSelect,
    isPhone,
    sizing,
    onViewPlan,
    canViewPlan = false,
    planEntry,
    planViewOpen,
    onClosePlanView,
  } = props;
  const activeAsset = assets[activeIndex] || null;
  const hasActiveAsset = Boolean(activeAsset);
  const kind = getAssetKind(activeAsset);
  const activeAssetIsSvg = isSvgAsset(activeAsset);
  const previewUrl = getAssetPreviewUrl(activeAsset);
  const normalizedActiveImageUrl = useNormalizedGalleryAsset(activeAsset);
  const thumbnailRailHeight = assets.length > 1 ? (isPhone ? 84 : 96) : 0;
  const dialogPadding = isPhone ? 16 : 24;
  const phoneNavOffset = Number(sizing?.xs?.usedWidth || 0);
  const mediaMaxHeight = `calc(${isPhone ? "100vh" : "92vh"} - ${thumbnailRailHeight}px - ${dialogPadding * 2}px)`;
  const navButtonSx = {
    backgroundColor: "rgba(17,22,29,0.72)",
    color: "#edf3fb",
    "&:hover": {
      backgroundColor: "rgba(17,22,29,0.9)",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted
      maxWidth="lg"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{
        sx: {
          backgroundColor: "#11161d",
          color: "#edf3fb",
          borderRadius: isPhone ? 0 : "24px",
          overflow: "hidden",
          height: isPhone ? "100vh" : "min(92vh, 980px)",
          maxHeight: isPhone ? "100vh" : "92vh",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            pr: isPhone ? `${phoneNavOffset}px` : 0,
            boxSizing: "border-box",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              zIndex: 3,
              backgroundColor: "rgba(17,22,29,0.72)",
              color: "#edf3fb",
              "&:hover": {
                backgroundColor: "rgba(17,22,29,0.9)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              minHeight: 0,
              px: { xs: 1, sm: 3 },
              py: { xs: 1, sm: 2 },
              background:
                "radial-gradient(circle at top, rgba(86,111,145,0.24), rgba(17,22,29,0.96) 60%)",
              overflow: { xs: "hidden", md: "visible" },
            }}
          >
            {!isPhone && (
              <IconButton
                onClick={onPrev}
                disabled={assets.length < 2}
                sx={{
                  position: "absolute",
                  left: { xs: 8, sm: 18 },
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  ...navButtonSx,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            {!hasActiveAsset ? (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 920,
                  maxHeight: mediaMaxHeight,
                  minHeight: 240,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "20px",
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                <Typography variant="body1" sx={{ color: "#d7e0eb" }}>
                  No asset selected
                </Typography>
              </Box>
            ) : kind === "pdf" && !previewUrl ? (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 920,
                  maxHeight: mediaMaxHeight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 2,
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "20px",
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 68, color: "#d5c0a3" }} />
                <Typography variant="body1">PDF asset</Typography>
                <Button
                  component="a"
                  href={activeAsset?.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{
                    backgroundColor: "#5B7195",
                    "&:hover": { backgroundColor: "#4a5d7a" },
                  }}
                >
                  Open PDF
                </Button>
              </Box>
            ) : kind === "pdf" ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    maxWidth: "100%",
                    maxHeight: `calc(${mediaMaxHeight} - 56px)`,
                  }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={activeAsset?.fileName || `PDF preview ${activeIndex + 1}`}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: `calc(${mediaMaxHeight} - 56px)`,
                      borderRadius: "18px",
                      boxShadow: "0 24px 40px rgba(0,0,0,0.28)",
                    }}
                  />
                  {!planViewOpen && canViewPlan && (
                    <Fab
                      color="primary"
                      size="medium"
                      onClick={onViewPlan}
                      sx={{
                        position: "absolute",
                        right: { xs: 12, sm: 16 },
                        bottom: { xs: 12, sm: 16 },
                        zIndex: 2,
                        minHeight: 0,
                        width: "auto",
                        height: { xs: 42, sm: 46 },
                        px: { xs: 2, sm: 2.25 },
                        borderRadius: "12px",
                        boxShadow: "0 14px 28px rgba(0,0,0,0.28)",
                        backgroundColor: "rgba(91,113,149,0.96)",
                        color: "#f6f1e8",
                        backdropFilter: "blur(8px)",
                        fontSize: { xs: 12, sm: 13 },
                        fontWeight: 700,
                        lineHeight: 1.1,
                        textAlign: "center",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: "rgba(74,93,122,1)",
                        },
                      }}
                    >
                      View Plan
                    </Fab>
                  )}
                </Box>
                <Button
                  component="a"
                  href={buildPdfEmbedUrl(activeAsset?.url || "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(237,243,251,0.3)",
                    color: "#edf3fb",
                  }}
                >
                  Open Full PDF
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  maxWidth: "100%",
                  maxHeight: mediaMaxHeight,
                }}
              >
                <Box
                  component="img"
                  src={normalizedActiveImageUrl || activeAsset?.url || ""}
                  alt={activeAsset?.fileName || `Image ${activeIndex + 1}`}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: mediaMaxHeight,
                    borderRadius: "20px",
                    boxShadow: "0 24px 40px rgba(0,0,0,0.3)",
                    backgroundColor: activeAssetIsSvg ? "#ffffff" : "transparent",
                  }}
                />
                {!planViewOpen && canViewPlan && (
                  <Fab
                    color="primary"
                    size="medium"
                    onClick={onViewPlan}
                    sx={{
                      position: "absolute",
                      right: { xs: 12, sm: 16 },
                      bottom: { xs: 12, sm: 16 },
                      zIndex: 2,
                      minHeight: 0,
                      width: "auto",
                      height: { xs: 42, sm: 46 },
                      px: { xs: 2, sm: 2.25 },
                      borderRadius: "12px",
                      boxShadow: "0 14px 28px rgba(0,0,0,0.28)",
                      backgroundColor: "rgba(91,113,149,0.96)",
                      color: "#f6f1e8",
                      backdropFilter: "blur(8px)",
                      fontSize: { xs: 12, sm: 13 },
                      fontWeight: 700,
                      lineHeight: 1.1,
                      textAlign: "center",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        backgroundColor: "rgba(74,93,122,1)",
                      },
                    }}
                  >
                    View Plan
                  </Fab>
                )}
              </Box>
            )}

            {!isPhone && (
              <IconButton
                onClick={onNext}
                disabled={assets.length < 2}
                sx={{
                  position: "absolute",
                  right: { xs: 8, sm: 18 },
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  ...navButtonSx,
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}
          </Box>

          {isPhone && assets.length > 1 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                px: 2,
                pb: 1,
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <IconButton onClick={onPrev} disabled={assets.length < 2} sx={navButtonSx}>
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton onClick={onNext} disabled={assets.length < 2} sx={navButtonSx}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {assets.length > 1 && (
            <Box
              sx={{
                px: { xs: 1, sm: 2 },
                py: { xs: 1, sm: 1.5 },
                overflowX: "auto",
                overflowY: "hidden",
                flexShrink: 0,
                scrollbarColor: "#8ea6c9 rgba(255,255,255,0.08)",
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": {
                  height: 10,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(255,255,255,0.08)",
                  borderRadius: 999,
                },
                "&::-webkit-scrollbar-thumb": {
                  background:
                    "linear-gradient(90deg, rgba(142,166,201,0.92), rgba(213,192,163,0.92))",
                  borderRadius: 999,
                  border: "2px solid rgba(17,22,29,0.92)",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background:
                    "linear-gradient(90deg, rgba(156,180,217,1), rgba(223,202,173,1))",
                },
              }}
            >
              <Box sx={{ display: "flex", gap: 1, minWidth: "max-content" }}>
                {assets.map((asset, index) => {
                  const thumbKind = getAssetKind(asset);
                  const thumbIsSvg = isSvgAsset(asset);
                  const thumbUrl = getAssetPreviewUrl(asset) || asset.url;
                  const isActive = index === activeIndex;

                  return (
                    <Box
                      key={getAssetKey(asset, index)}
                      component="button"
                      type="button"
                      onClick={() => onSelect(index)}
                      sx={{
                        width: 72,
                        height: 72,
                        p: 0,
                        borderRadius: "14px",
                        overflow: "hidden",
                        border: isActive
                          ? "2px solid #8ea6c9"
                          : "1px solid rgba(255,255,255,0.12)",
                        backgroundColor: "rgba(255,255,255,0.03)",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {thumbUrl ? (
                        <Box
                          component="img"
                          src={thumbUrl}
                          alt={asset?.fileName || `Preview ${index + 1}`}
                          sx={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            backgroundColor: thumbIsSvg ? "#ffffff" : "transparent",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#edf3fb",
                          }}
                        >
                          {thumbKind === "pdf" ? (
                            <DescriptionOutlinedIcon fontSize="small" />
                          ) : (
                            <ImageOutlinedIcon fontSize="small" />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        <GalleryPlanOverlay
          open={planViewOpen}
          entry={planEntry}
          isPhone={isPhone}
          sizing={sizing}
          onBack={onClosePlanView}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

export default GalleryViewerDialog;
